import classNames from "classnames";
import React, {
  useState,
  ComponentPropsWithRef,
  useRef,
  ReactNode,
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from "react";

import styles from "./index.module.css";
import SvgIcon from "@/components/common/SvgIcon";
import { Typography } from "@/components/kit";

export type TTextFieldProps = Readonly<
  ComponentPropsWithRef<"textarea"> & {
    error?: string | boolean;
    icon?: ReactNode | string;
    onEnter?: () => void;
    endAdornment?: ReactNode | string;
    label?: ReactNode | string;
    onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    disabled?: boolean;
    isRequired?: boolean;
    containerClassName?: string;
  }
>;

const TextArea = forwardRef((props: TTextFieldProps, ref) => {
  const {
    name,
    value = "",
    icon,
    onChange = () => null,
    onBlur = () => null,
    onEnter = () => null,
    onFocus = () => null,
    disabled = false,
    label,
    error,
    endAdornment,
    className,
    containerClassName,
    isRequired = false,
    ...restProps
  } = props;
  const [isFocus, setIsFocus] = useState(false);
  const textboxRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => textboxRef.current!, []);
  const activeFocus = (e: any) => {
    if (disabled) return;
    onFocus(e);
    if (textboxRef.current) textboxRef.current?.focus();
    setIsFocus(true);
  };

  const deactiveFocus = () => setIsFocus(false);

  const onKeyDownHandle = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    if (e.key === "Enter") {
      onEnter();
    }
  };

  const onLocalChange = (e: ChangeEvent<HTMLTextAreaElement>) => onChange(e);

  return (
    <div
      className={classNames("w-full select-none leading-6", containerClassName)}
    >
      <div
        className={classNames(`${styles.textbox}`, {
          [styles.err]: !!error,
          [styles.active]: !!label && (isFocus || value || value === 0),
        })}
      >
        <textarea
          {...restProps}
          ref={textboxRef}
          className={classNames(
            "disabled:border-gray-200 dark:text-gray-100 disabled:shadow-none disabled:opacity-30 disabled:bg-gray-100 transition-all hover:border-gray-300 focus:!border-blue-400 focus:border-[2px]",
            { "!pt-4": !!label },
            className,
            { "!text-[unset]": isFocus },
          )}
          onChange={onLocalChange}
          onKeyDown={onKeyDownHandle}
          value={value}
          onFocus={activeFocus}
          onBlur={(e) => {
            deactiveFocus();
            onBlur(e);
          }}
          name={name}
          disabled={disabled}
        />
        {!!label && (
          <label
            className={`${disabled ? "opacity-30 text-gray-400" : ""}`}
            onClick={activeFocus}
            htmlFor={name}
          >
            {typeof icon !== "string" && icon && <>{icon}</>}
            {typeof icon === "string" && icon && <img src={icon} />}

            <div className={"flex"}>
              {label}
              {isRequired && <span className={"ml-0.5 text-[16px]"}>*</span>}
            </div>
          </label>
        )}
        {!!endAdornment && (
          <div className="absolute inset-y-0  flex justify-center items-center right-4">
            {endAdornment}
          </div>
        )}
      </div>

      {!!error && (
        <div className="error ps-2 pt-2 flex justify-start items-center gap-1">
          <SvgIcon
            className={"w-3 h-3"}
            fill="fill-red-600"
            iconName="attentionRed"
          />
          <Typography color={"text-red-600"} variant={"Caption1-Regular"}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
});

TextArea.displayName = "TextArea";

export default TextArea;
